import React, { useState } from 'react';

import { Box, Typography } from '@material-ui/core';

import { AppChip } from '@vyce/core/src/components/AppChip';
import { formatPhoneNumber } from '@vyce/core/src/utils/phone';

import { InviteSelectionApi } from '../hooks/useInviteSelection';
import useStyles from '../styles';
import { getBulkLabel } from '../utils/labels';

const CHIP_LIMIT = 8;

interface Props {
  selection: InviteSelectionApi;
}

/** Who is about to be invited — every chip is removable, so step 2 doubles as a review. */
export const SelectionSummary: React.FC<Props> = ({ selection }) => {
  const classes = useStyles();
  const [showAll, setShowAll] = useState<boolean>(false);
  const { phones, picked, bulk } = selection.state;
  const people = Array.from(picked.values());
  // A pasted list can hold hundreds of numbers, so phones collapse the same way people do
  const visiblePeople = showAll ? people : people.slice(0, CHIP_LIMIT);
  const visiblePhones = showAll ? phones : phones.slice(0, CHIP_LIMIT);
  const more = people.length - visiblePeople.length + (phones.length - visiblePhones.length);

  if (!phones.length && !people.length && !bulk) {
    return null;
  }

  return (
    <Box display="flex" flexDirection="column" gridGap={8}>
      <Typography variant="body2" color="textSecondary">
        Inviting:
      </Typography>

      <Box display="flex" flexWrap="wrap" alignItems="center" gridGap={8}>
        {bulk && <AppChip label={getBulkLabel(bulk)} onDelete={selection.clearBulk} />}

        {visiblePeople.map(worker => (
          <AppChip
            key={worker.user_id}
            label={`${worker.first_name} ${worker.last_name}`.trim()}
            onDelete={() => selection.removeWorker(worker.user_id)}
          />
        ))}

        {visiblePhones.map(phone => (
          <AppChip
            key={phone}
            label={formatPhoneNumber(phone)}
            onDelete={() => selection.removePhone(phone)}
          />
        ))}

        {more > 0 && (
          <Typography className={classes.more} onClick={() => setShowAll(true)}>
            + {more} more
          </Typography>
        )}
        {showAll && (people.length > CHIP_LIMIT || phones.length > CHIP_LIMIT) && (
          <Typography className={classes.more} onClick={() => setShowAll(false)}>
            less
          </Typography>
        )}
      </Box>
    </Box>
  );
};
